import { useEffect, useState } from 'react'
import { useBilling } from '../contexts/BillingContext'
import { BillingConfig } from '../types/billing'

/**
 * Editable copy of one or more settings groups. Loads the saved values, tracks
 * unsaved changes, and saves through the audited settings transaction.
 */
export function useSettingsDraft<K extends keyof BillingConfig>(keys: K[], label: string, validate?: (draft: Pick<BillingConfig, K>) => string | null) {
  const { config, saveConfig, can } = useBilling()
  const pick = () => Object.fromEntries(keys.map((k) => [k, structuredClone(config[k])])) as Pick<BillingConfig, K>
  const [draft, setDraft] = useState<Pick<BillingConfig, K>>(pick)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null)

  // Pick up saved changes from other users while the form is untouched.
  useEffect(() => {
    if (!dirty) setDraft(pick())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  const update = <G extends K>(group: G, patch: Partial<BillingConfig[G]>) => {
    setDraft((d) => ({ ...d, [group]: Array.isArray(d[group]) ? patch : { ...(d[group] as object), ...patch } }))
    setDirty(true)
    setStatus(null)
  }
  const replace = <G extends K>(group: G, value: BillingConfig[G]) => {
    setDraft((d) => ({ ...d, [group]: value }))
    setDirty(true)
    setStatus(null)
  }
  const reset = () => { setDraft(pick()); setDirty(false); setStatus(null) }
  const save = async () => {
    const problem = validate?.(draft)
    if (problem) return setStatus({ ok: false, text: problem })
    setSaving(true)
    const result = await saveConfig(draft as Partial<BillingConfig>, label)
    setSaving(false)
    if (!result.ok) return setStatus({ ok: false, text: `${result.error ?? 'Not saved.'} Your changes are still here.` })
    setDirty(false)
    setStatus({ ok: true, text: result.message ?? 'Saved.' })
  }
  return { draft, update, replace, reset, save, dirty, saving, status, canEdit: can('manageSettings') }
}
